import { useState, useEffect, useRef } from 'react';
import { Download, Upload, X, AlertTriangle } from 'lucide-react';

import { cardSettlementApi } from '@/api/cardSettlement.api';
import { cardAcquirerApi }   from '@/api/cardAcquirer.api';
import { Button }            from '@/components/ui/Button';
import { Input }             from '@/components/ui/Input';
import { Select }            from '@/components/ui/Select';
import { Spinner }           from '@/components/ui/Spinner';
import { cn }                from '@/utils/cn';

import {
  downloadCardSettlementTemplate, parseCardSettlementFile, summarise,
} from '@/utils/cardSettlementSheet';
import type { CardSettlementImportRowDto, CardAcquirerListDto } from '@/types/cardReconciliation.types';

interface Props {
  onImported: () => void;
  onClose:    () => void;
}

/**
 * Importing an acquirer's settlement file.
 *
 * The screen is called Card Settlement Import and, until now, only listed batches that were
 * already there — with no way to create one. The endpoint has always existed; its contract is a
 * list of rows rather than a file, so the sheet is read here and the totals shown before anything
 * is sent. Nobody should post a settlement they have not seen the shape of.
 */
export const CardSettlementImportPanel = ({ onImported, onClose }: Props) => {
  const fileInput = useRef<HTMLInputElement>(null);

  const [acquirers, setAcquirers] = useState<CardAcquirerListDto[]>([]);
  const [acquirerId, setAcquirerId] = useState('');
  const [batchNo,    setBatchNo]    = useState('');
  const [remarks,    setRemarks]    = useState('');

  const [fileName, setFileName] = useState<string | null>(null);
  const [rows,     setRows]     = useState<CardSettlementImportRowDto[]>([]);
  const [problems, setProblems] = useState<{ rowNumber: number; message: string }[]>([]);

  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done,  setDone]  = useState<string | null>(null);

  useEffect(() => {
    cardAcquirerApi.getList(true)
      .then(res => setAcquirers(res.data?.data ?? []))
      .catch(() => setError('Could not load the acquirer list.'));
  }, []);

  const readFile = async (file: File) => {
    setError(null); setDone(null);
    setBusy(true);
    try {
      const parsed = await parseCardSettlementFile(file);
      setRows(parsed.rows);
      setProblems(parsed.problems);
      setFileName(file.name);
      // The acquirer's own file name is the most useful default anyone has for the batch number.
      if (!batchNo) setBatchNo(file.name.replace(/\.[^.]+$/, '').slice(0, 40));
    } catch (err) {
      setRows([]); setProblems([]); setFileName(null);
      setError(err instanceof Error ? err.message : 'That file could not be read.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!acquirerId)      { setError('Choose which acquirer this file came from.'); return; }
    if (!batchNo.trim())  { setError('Give the batch a reference.'); return; }
    if (rows.length === 0) { setError('There are no settlement lines to import.'); return; }

    setBusy(true); setError(null);
    try {
      await cardSettlementApi.import({
        settlementBatchNo: batchNo.trim(),
        cardAcquirerId:    acquirerId,
        fileReference:     fileName,
        importSource:      'Spreadsheet',
        remarks:           remarks.trim() || null,
        rows,
      });
      setDone(`${rows.length} line${rows.length === 1 ? '' : 's'} imported.`);
      setRows([]); setProblems([]); setFileName(null); setBatchNo(''); setRemarks('');
      onImported();
    } catch (err) {
      const body = (err as { response?: { data?: { message?: string;
                     errors?: { message?: string }[] } } }).response?.data;
      setError(body?.errors?.[0]?.message ?? body?.message ?? 'The import was refused.');
    } finally {
      setBusy(false);
    }
  };

  const totals = summarise(rows);
  const money  = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Import a settlement file</h2>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={downloadCardSettlementTemplate}
                  leftIcon={<Download className="w-3.5 h-3.5" />}>
            Download template
          </Button>
          <button onClick={onClose} aria-label="Close"
                  className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select
            label="Acquirer" value={acquirerId} placeholder="— choose —"
            onChange={e => setAcquirerId(e.target.value)}
            options={acquirers.map(a => ({ value: a.id, label: `${a.acquirerCode} — ${a.acquirerName}` }))}
          />
          <Input label="Batch reference" value={batchNo} maxLength={50}
                 onChange={e => setBatchNo(e.target.value)} placeholder="e.g. SETTLE-2026-09-15" />
          <Input label="Remarks" value={remarks} maxLength={200}
                 onChange={e => setRemarks(e.target.value)} placeholder="Optional" />
        </div>

        {/* Drop zone */}
        <div
          onClick={() => fileInput.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) readFile(f); }}
          className={cn(
            'border-2 border-dashed rounded-lg px-6 py-8 text-center cursor-pointer transition-colors',
            'border-slate-300 dark:border-slate-600 hover:border-teal-500 hover:bg-teal-50/40',
            'dark:hover:bg-teal-900/10',
          )}
        >
          <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-700 dark:text-slate-300">
            {fileName ?? 'Drop the acquirer’s .xlsx or .csv here, or click to choose'}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            It needs at least Settlement Date and Gross Amount columns. Download the template to
            see the full set.
          </p>
          <input ref={fileInput} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                 onChange={e => { const f = e.target.files?.[0]; if (f) readFile(f); e.target.value = ''; }} />
        </div>

        {busy && <div className="flex justify-center py-3"><Spinner /></div>}

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {done && (
          <div className="rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-4 py-3 text-sm text-green-700 dark:text-green-300">
            {done}
          </div>
        )}

        {problems.length > 0 && (
          <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-300">
              <AlertTriangle className="w-4 h-4" />
              {problems.length} row{problems.length === 1 ? '' : 's'} could not be read and
              {problems.length === 1 ? ' is' : ' are'} not included
            </div>
            <ul className="mt-2 text-xs text-amber-700 dark:text-amber-400 space-y-0.5">
              {problems.slice(0, 8).map(p => (
                <li key={p.rowNumber}>Row {p.rowNumber}: {p.message}</li>
              ))}
              {problems.length > 8 && <li>… and {problems.length - 8} more</li>}
            </ul>
          </div>
        )}

        {rows.length > 0 && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {([
                ['Lines',      String(rows.length)],
                ['Gross',      money(totals.gross)],
                ['MDR',        money(totals.mdr)],
                ['Chargeback', money(totals.chargeback)],
                ['Net',        money(totals.net)],
              ] as const).map(([label, value]) => (
                <div key={label} className="rounded-md bg-slate-50 dark:bg-slate-900/40 px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
                  <div className="text-sm font-semibold text-slate-900 dark:text-white tabular-nums">{value}</div>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-900/40">
                  <tr className="text-slate-500 dark:text-gray-400">
                    {['#', 'Date', 'Terminal', 'Card', 'Gross', 'MDR', 'Chargeback'].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-semibold uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {rows.slice(0, 10).map(r => (
                    <tr key={r.lineNo} className="text-slate-700 dark:text-slate-300">
                      <td className="px-3 py-1.5 text-slate-400">{r.lineNo}</td>
                      <td className="px-3 py-1.5">{r.settlementDate}</td>
                      <td className="px-3 py-1.5">{r.terminalId ?? '—'}</td>
                      <td className="px-3 py-1.5">{r.cardTypeName ?? '—'}</td>
                      <td className="px-3 py-1.5 tabular-nums">{money(r.grossAmount)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{money(r.mdrAmount)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{money(r.chargebackAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 10 && (
                <div className="px-3 py-2 text-xs text-slate-400 border-t border-slate-100 dark:border-slate-800">
                  Showing the first 10 of {rows.length} lines.
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => { setRows([]); setProblems([]); setFileName(null); }}>
                Clear
              </Button>
              <Button onClick={submit} loading={busy} disabled={busy}>
                Import {rows.length} line{rows.length === 1 ? '' : 's'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
